/** @jsxImportSource @emotion/react */
import { useSelector } from "react-redux";
import * as authSelectors from "../auth/authSelectors";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useGetShop, useLinkProductToShop } from "./service/shop-api";
import { useNaturalSortByName } from "../common/helpers/useNaturalSortBy";
import { SpinnerBlock } from "../common/Spinner";
import { css } from "@emotion/react";
import { CardContainer } from "../../components/page-skeleton/CardContainer";
import { PageHeader } from "../../components/page-skeleton/PageHeader";
import { ProductsFilter } from "../products/ProductsFilter";
import { SearchTextFieldWithKeyboardInteraction } from "../../components/forms/fields/SearchTextFieldWithKeyboardInteraction";
import { ShopProductsListTable } from "./ShopProductsListTable";
import { useListShopProducts } from "../products/service/products-api";
import { getErrorMessage, useHandleFetchError } from "../../error-handler";
import { Divider } from "../../components/Divider";
import { SelectProductsSelector } from "../../components/SelectProductsSelector";
import { useState } from "react";
import { ProductListItem } from "../products/models/product";
import { toastr } from "react-redux-toastr";
import { sanitizeString } from "../../util";
import { CardBody } from "../../components/page-skeleton/CardBody";
import { EmptyState } from "../../components/EmptyState";

const textFieldStyling = css`
  width: 100%;
`;

const cardBodyAdditionalStyling = css`
  padding-bottom: 24px;
`;

// Hacking width until all cards on page are updated
const cardAdditionalStyling = css`
  max-width: 1174px !important;
`;

// Normally we would use PageWrapper component, but other components on the page are not updated yet
const cardWrapperStyle = css`
  padding-left: 24px;
  padding-right: 24px;
  display: flex;
  width: 100%;
  justify-content: center;
`;

type AddProductsForm = {
  products: string[];
};

const filterProducts = (products: ProductListItem[], searchValue: string) => {
  return products.filter((p) =>
    sanitizeString(p.name.toLowerCase()).includes(sanitizeString(searchValue))
  );
};

export const ShopProductsList = () => {
  const [isLoadingBulkLink, setIsLoadingBulkLink] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const { shopId } = useParams<{
    shopId: string | undefined;
  }>();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  const { mutateAsync: linkProductToShop } = useLinkProductToShop();

  const onSubmit = async (products: ProductListItem[]) => {
    setIsLoadingBulkLink(true);
    if (!shopId) {
      setIsLoadingBulkLink(false);
      throw new Error(
        "Link product to shop only available on Venue Menu screen"
      );
    }
    for (let index = 0; index < products.length; index++) {
      const request = {
        organizationKeyName,
        productId: products[index].id,
        shopId: shopId,
      };
      try {
        await linkProductToShop(request);
        toastr.success(
          "Success",
          "You successfully added the product to the restaurant"
        );
      } catch (error: unknown) {
        const errorMessage = getErrorMessage(error);
        toastr.error("Error", errorMessage);
        continue;
      }
    }
    setIsLoadingBulkLink(false);
  };

  const methods = useForm<AddProductsForm>({
    defaultValues: {
      products: [],
    },
  });

  const { data, isLoading, error: productsError } = useListShopProducts(
    organizationKeyName,
    {
      classification: "admin-main-list",
      shopId,
    }
  );

  const { data: shop, isLoading: isLoadingShop, error: shopError } = useGetShop(
    organizationKeyName,
    shopId
  );

  const sortedProducts = useNaturalSortByName(data);
  const productsToHide = sortedProducts.map((product) => product.id);

  // Using venueid to determine if a shop is "physical" or "digital".
  // If physical show generic products, if digital show vouchers
  const classificationFilter = shop?.venueId ? "generic" : "admin-vouchers";

  useHandleFetchError(shopError);
  useHandleFetchError(productsError);

  if (isLoading || isLoadingShop || !shopId) {
    return <SpinnerBlock />;
  }

  return (
    <FormProvider {...methods}>
      <div css={cardWrapperStyle}>
        <CardContainer additionalStyling={cardAdditionalStyling}>
          <PageHeader title="Restaurant menu">
            {sortedProducts.length > 0 && (
              <>
                <div css={textFieldStyling}>
                  <SearchTextFieldWithKeyboardInteraction
                    searchValue={searchValue || ""}
                    setSearchValue={setSearchValue}
                    placeholder="Search for product name"
                  />
                </div>
              </>
            )}
            <SelectProductsSelector
              filters={{ classification: classificationFilter }}
              buttonText="Add product"
              modalHeader="Add product"
              isLoadingSave={isLoadingBulkLink}
              onSave={onSubmit}
              productIdsToLeaveOut={productsToHide}
            />
          </PageHeader>
          {sortedProducts.length > 0 ? (
            <>
              <div>
                <Divider />
                <ProductsFilter
                  additionalStyling={css`
                    padding: 16px 24px;
                  `}
                />
                <ShopProductsListTable
                  shopId={shopId}
                  products={filterProducts(sortedProducts, searchValue)}
                />
              </div>
            </>
          ) : (
            <CardBody css={cardBodyAdditionalStyling}>
              <EmptyState
                title="No products found"
                subtitle="No products found on the restaurant menu"
              />
            </CardBody>
          )}
        </CardContainer>
      </div>
    </FormProvider>
  );
};
